import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, User, Building2, ArrowRight, CheckCircle } from 'lucide-react';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const initTab = searchParams.get('tab') === 'partner' ? 'partner' : searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [tab, setTab] = useState(initTab);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === 'Admin' ? '/admin' : user.role === 'Partner' ? '/partner' : '/');
  }, [user]);

  const handleField = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let res;
      if (tab === 'login') {
        res = await authAPI.login({ email: form.email, password: form.password });
      } else {
        res = await authAPI.register({
          name: form.name, email: form.email,
          phone: form.phone, password: form.password,
          role: tab === 'partner' ? 'Partner' : 'Customer'
        });
      }
      login(res.token, res.user);
      if (res.user.role === 'Admin') navigate('/admin');
      else if (res.user.role === 'Partner') navigate('/partner');
      else navigate('/');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isRegister = tab === 'register' || tab === 'partner';

  return (
    <div data-testid="auth-page" className="min-h-screen flex">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy noise-overlay relative overflow-hidden flex-col justify-between p-16">
        <div className="relative z-10">
          <img src={LOGO} alt="HYGAD" className="h-12 mb-16" />
          <div className="mb-8">
            <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-4">
              {tab === 'partner' ? 'Join as Partner' : 'Welcome to HYGAD'}
            </span>
            <h2 className="font-serif text-4xl font-black text-white leading-tight tracking-tight">
              {tab === 'partner'
                ? 'List your property.\nReach thousands.'
                : tab === 'login'
                  ? 'Welcome back to\nthe region\'s finest.'
                  : 'Discover AP &\nTelangana with us.'}
            </h2>
          </div>
          <p className="text-white/50 text-sm leading-relaxed max-w-xs">
            {tab === 'partner'
              ? 'Pay a one-time onboarding fee to get your property verified and listed in front of thousands of travelers and event planners across the region.'
              : 'From intimate homestays to grand celebration venues — HYGAD is your gateway to the best of Andhra Pradesh and Telangana.'}
          </p>
        </div>

        {/* Feature bullets for partner */}
        {tab === 'partner' && (
          <div className="relative z-10 space-y-3">
            {['Business Verification', 'Property Listing on HYGAD', 'Direct Enquiry Alerts', 'Partner Dashboard & Analytics', 'One-Time Onboarding Fee'].map(f => (
              <div key={f} className="flex items-center gap-3">
                <CheckCircle size={15} className="text-gold shrink-0" />
                <span className="text-white/60 text-sm">{f}</span>
              </div>
            ))}
          </div>
        )}

        {/* Background image */}
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1607567618395-62fc2d132c3e?w=900&q=60"
            alt="" className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <img src={LOGO} alt="HYGAD" className="h-10" />
          </div>

          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
            {[
              { key: 'login', label: 'Sign In', icon: <User size={14} /> },
              { key: 'register', label: 'Customer', icon: <User size={14} /> },
              { key: 'partner', label: 'Partner', icon: <Building2 size={14} /> },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                data-testid={`auth-tab-${key}`}
                onClick={() => { setTab(key); setError(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  tab === key ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'
                }`}
              >
                {icon} {label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="mb-7">
                <h2 className="font-serif text-3xl font-bold text-navy tracking-tight">
                  {tab === 'login' ? 'Sign in' : tab === 'partner' ? 'Become a Partner' : 'Create Account'}
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  {tab === 'login'
                    ? 'Welcome back to HYGAD'
                    : tab === 'partner'
                      ? 'List your property and reach more guests'
                      : 'Join HYGAD to discover amazing stays'}
                </p>
              </div>

              <form data-testid="auth-form" onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1.5">Full Name *</label>
                    <input
                      data-testid="auth-name-input"
                      required type="text" placeholder="Your full name"
                      value={form.name} onChange={handleField('name')}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold transition-colors"
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Email Address *</label>
                  <input
                    data-testid="auth-email-input"
                    required type="email" placeholder="you@example.com"
                    value={form.email} onChange={handleField('email')}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold transition-colors"
                  />
                </div>

                {isRegister && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1.5">Phone Number</label>
                    <input
                      data-testid="auth-phone-input"
                      type="tel" placeholder="+91 9xxxxxxxxx"
                      value={form.phone} onChange={handleField('phone')}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold transition-colors"
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Password *</label>
                  <div className="relative">
                    <input
                      data-testid="auth-password-input"
                      required type={showPass ? 'text' : 'password'}
                      placeholder={isRegister ? 'Min 6 characters' : 'Your password'}
                      value={form.password} onChange={handleField('password')}
                      minLength={isRegister ? 6 : 1}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm text-navy outline-none focus:border-gold transition-colors"
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy transition-colors">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.p
                    data-testid="auth-error"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg"
                  >
                    {error}
                  </motion.p>
                )}

                <button
                  data-testid="auth-submit"
                  type="submit" disabled={loading}
                  className="w-full bg-navy hover:bg-navy-light text-white py-3.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
                >
                  {loading
                    ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <>
                      {tab === 'login' ? 'Sign In' : tab === 'partner' ? 'Create Partner Account' : 'Create Account'}
                      <ArrowRight size={15} />
                    </>
                  }
                </button>

                {tab === 'login' && (
                  <p className="text-center text-xs text-gray-400">
                    Don't have an account?{' '}
                    <button type="button" onClick={() => setTab('register')} className="text-gold font-medium hover:underline">
                      Sign up
                    </button>
                  </p>
                )}
                {isRegister && (
                  <p className="text-center text-xs text-gray-400">
                    Already have an account?{' '}
                    <button type="button" onClick={() => setTab('login')} className="text-gold font-medium hover:underline">
                      Sign in
                    </button>
                  </p>
                )}

                {tab === 'partner' && (
                  <div className="mt-4 bg-gold/5 border border-gold/20 rounded-xl p-4">
                    <p className="text-xs text-navy font-medium mb-1">One-Time Partner Onboarding Fee</p>
                    <p className="text-xs text-gray-500">Business verification · Property listing · Direct enquiries · Dashboard access · Priority support.</p>
                  </div>
                )}
              </form>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Auth;
